<script lang="ts">
  import type { Snapshot } from './$types';
  import { restoreHomeScroll, type HomeScrollPosition } from '$lib/utils/home-scroll';

  export const snapshot: Snapshot<HomeScrollPosition> = {
    capture: () => ({ top: window.scrollY, left: window.scrollX }),
    restore: restoreHomeScroll
  };
  import ArrowUpRight from '@lucide/svelte/icons/arrow-up-right';
  import ArrowDown from '@lucide/svelte/icons/arrow-down';
  import Check from '@lucide/svelte/icons/check';
  import BookOpen from '@lucide/svelte/icons/book-open';
  import Video from '@lucide/svelte/icons/video';
  import CalendarDays from '@lucide/svelte/icons/calendar-days';
  import Users from '@lucide/svelte/icons/users';
  import AppWorkspace from '$lib/components/landing/app-workspace.svelte';
  import PlatformShowcase from '$lib/components/landing/platform-showcase.svelte';
  import MotionAction from '$lib/components/landing/effects/motion-action.svelte';
  import ContactSection from '$lib/components/site/contact-section.svelte';
  import FaqSection from '$lib/components/site/faq-section.svelte';
  import { JsonLd } from 'svelte-meta-tags';
  import { HOME_FAQ } from '$lib/utils/faq-content';
  import { faqSchema, softwareSchema, webPageSchema } from '$lib/utils/structured-data';
  import PhoneFrame from '$lib/components/site/phone-frame.svelte';
  import MobileApp from '$lib/components/landing/mobile-app.svelte';

  const tools = [
    {
      title: 'Teaching & learning',
      items: [
        'Structured courses',
        'Video and PDF lessons',
        'Live classes',
        'Exercises and submissions',
        'Grades and teacher feedback',
        'Learning progress and certificates'
      ]
    },
    {
      title: 'Institution operations',
      items: [
        'Teacher and student management',
        'Batches and enrollment',
        'Recurring timetables',
        'Manual attendance',
        'Materials and batch notices',
        'Guardian relationships'
      ]
    },
    {
      title: 'Your learning community',
      items: [
        'Course-linked discussions',
        'Questions, replies and upvotes',
        'Institution website',
        'Brand and layout controls',
        'Course catalogue',
        'Role-based access'
      ]
    }
  ];
  const pageSchema = [webPageSchema('home'), softwareSchema(), faqSchema(HOME_FAQ)];
</script>

<JsonLd schema={pageSchema} />

<div class="landing">
  <section class="hero">
    <div class="hero-scene">
      <div class="hero-curtain" aria-hidden="true"></div>
      <div class="hero-copy">
        <h1>
          <span class="eyebrow hero-kicker" data-hero-fade>Edvello — coaching institute management software</span>
          <span class="hero-title-line">Your teaching.</span><span class="hero-title-line hero-serif"
            ><span class="hero-ink">All</span> <span class="hero-ink">together.</span></span
          >
        </h1>
        <p data-hero-fade>
          Edvello is a learning platform for coaching institutes and tuition centres in India. Organise courses and
          batches in one workspace. Record attendance and review student work.
        </p>
        <div class="hero-actions" data-hero-fade>
          <MotionAction /><a class="explore-link" href="#platform">Explore the platform <ArrowDown size={15} /></a>
        </div>
        <p class="audience-line" data-hero-fade>For tuition teachers, coaching institutes and schools across India.</p>
      </div>
      <div class="hero-product-wrap">
        <div class="hero-arc" aria-hidden="true"></div>
        <div class="hero-device-group">
          <div class="product-stage" data-workspace-entrance>
            <div class="stage-bar">
              <span class="stage-brand"
                ><img src="/brand/edvello-icon.svg" alt="" width="18" height="18" /> Your teaching workspace</span
              ><span class="stage-url">app.edvello.com</span><span class="stage-dots" aria-hidden="true"
                ><i></i><i></i><i></i></span
              >
            </div>
            <div class="desktop-screen"><AppWorkspace /></div>
            <PhoneFrame class="hero-phone" label="Edvello course screen on a phone, with sample data">
              <MobileApp view="courses" />
            </PhoneFrame>
          </div>
          <aside class="mobile-live-card" aria-label="Live Sessions preview">
            <div class="live-card-label"><span><Video size={14} /> Live Sessions</span></div>
            <h3>Functions &amp; graphs</h3>
            <p>Nov 4, 2026, 4:00 PM · Mathematics, made clear</p>
            <div class="live-card-bottom">
              <span><CalendarDays size={13} /> Starts in 2h</span><a href="#platform-feature-live">Join session</a>
            </div>
          </aside>
        </div>
        <div class="floating-note note-class" data-product-note>
          <span class="note-icon"><Video size={18} /></span>
          <div><strong>Make room for live learning.</strong><span>Sessions, materials and your students.</span></div>
        </div>
        <div class="floating-note note-feedback" data-product-note>
          <span class="note-icon green"><Check size={18} /></span>
          <div><strong>A little feedback. A step forward.</strong><span>Keep the learning moving.</span></div>
        </div>
      </div>
      <div class="mobile-hero-caption"><span>Your courses. Your classes.</span><strong>One workspace.</strong></div>
      <div class="workflow-strip">
        <span><BookOpen size={17} /> Create your courses</span><span class="connector" aria-hidden="true"></span><span
          ><CalendarDays size={17} /> Bring your batches together</span
        ><span class="connector" aria-hidden="true"></span><span><Users size={17} /> Teach. Learn. Stay connected.</span
        >
      </div>
      <section class="introduction">
        <div class="introduction-inner content-width">
          <h2>More time for teaching.<br />A clearer day for everyone.</h2>
          <div>
            <p>
              Your lesson notes, class schedules and student questions belong together. Edvello gives your team a shared
              place to manage the work around the learning.
            </p>
            <p>One institution workspace. A familiar experience for the people who teach, and the people who learn.</p>
          </div>
        </div>
      </section>
    </div>
  </section>

  <section class="teaching-statement" aria-label="Make room for teaching">
    <div class="statement-track" aria-hidden="true" data-marquee>
      <span>Make room</span><span class="statement-serif">for teaching.</span><svg viewBox="0 0 100 100" fill="none"
        ><path d="M50 5v90M5 50h90M18 18l64 64M18 82l64-64" stroke="currentColor" stroke-width="5" /></svg
      >
    </div>
    <div class="statement-bottom content-width">
      <p>Keep the class connected.<br />Let the learning take the lead.</p>
      <span class="scroll-cue"><ArrowDown size={16} /> Keep exploring</span>
    </div>
  </section>

  <PlatformShowcase />

  <section id="institutions" class="people-section content-width">
    <div class="people-heading">
      <h2>Built around the people<br />behind the learning.</h2>
      <p>Every role has a place.<br />Every course stays connected.</p>
    </div>
    <div class="people-layout">
      <div class="role-copy">
        <div>
          <span class="role-marker blue"></span>
          <h3>For institutions & schools</h3>
          <p>
            Give your team a common workspace. Organise teachers and batches, keep a consistent timetable and make your
            institution’s identity part of the experience.
          </p>
        </div>
        <div>
          <span class="role-marker purple"></span>
          <h3>For tuition teachers</h3>
          <p>
            Build your course, schedule the next class and review the work that follows. Keep materials and student
            conversations close to your teaching.
          </p>
        </div>
        <div>
          <span class="role-marker green"></span>
          <h3>For students & guardians</h3>
          <p>
            Students know what to learn next and where to find their course materials. Guardian relationships keep the
            institution connected to each student’s support.
          </p>
        </div>
      </div>
      <div class="roles-visual">
        <div class="roles-visual-top">
          <img src="/brand/edvello-icon.svg" alt="" width="27" height="27" /><span>Northfield Academy</span><span
            class="role-badge">Institution</span
          >
        </div>
        <h3>A shared space.<br />The right access.</h3>
        <div class="role-members">
          {#each [['MI', 'Meera Iyer', 'Teacher'], ['RS', 'Rohan Shah', 'Administrator'], ['KR', 'Kavya Rao', 'Student'], ['SR', 'Sanjay Rao', 'Guardian']] as member}<div
            >
              <span class="member-avatar">{member[0]}</span><strong>{member[1]}</strong><span>{member[2]}</span><Check
                size={14}
              />
            </div>{/each}
        </div>
        <p>Connected to the right courses and batches.</p>
      </div>
    </div>
  </section>

  <section class="feature-index">
    <div class="content-width">
      <div class="index-heading">
        <h2>The details that make<br />the day work.</h2>
        <a href="/product">Meet your platform <ArrowUpRight size={17} /></a>
      </div>
      <div class="index-columns">
        {#each tools as group}<div>
            <h3>{group.title}</h3>
            <ul>
              {#each group.items as item}<li><Check size={14} />{item}</li>{/each}
            </ul>
          </div>{/each}
      </div>
    </div>
  </section>

  <FaqSection items={HOME_FAQ} />

  <ContactSection />
</div>

<style>
  .landing {
    background: #f8faff;
    color: #192c49;
    overflow: clip;
    font-family: Geist, sans-serif;
  }
  .content-width {
    width: min(1120px, calc(100% - 80px));
    margin-inline: auto;
  }
  h1,
  h2,
  h3,
  p {
    margin: 0;
  }
  h1,
  h2 {
    text-wrap: balance;
    font-weight: 500;
    letter-spacing: -0.04em;
  }
  .hero {
    padding-top: 55px;
    position: relative;
    overflow: clip;
  }
  h1 > span {
    display: block;
  }
  h1 > .hero-kicker {
    display: flex;
    width: fit-content;
    margin: 0 auto 20px;
    font-size: 12px;
    line-height: 1.4;
    letter-spacing: 0.01em;
  }
  .hero-serif {
    font-family: 'Instrument Serif', Georgia, serif;
    font-size: 1.4em;
    font-style: italic;
    font-weight: 400;
    letter-spacing: -0.035em;
    line-height: 0.98;
    padding-bottom: 0.08em;
  }
  .hero-ink {
    display: inline-block;
    padding-right: 0.025em;
    margin-right: -0.025em;
  }
  @supports (background-clip: text) {
    .hero-ink {
      background: linear-gradient(100deg, #192c49 25%, #25446c 46%, #3c6094 50%, #25446c 54%, #192c49 75%);
      background-size: 200% 100%;
      background-position: 50% 0;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      color: transparent;
      animation: hero-ink-sweep 12s ease-in-out 1.8s infinite;
      animation-play-state: running;
    }
  }
  @keyframes hero-ink-sweep {
    0%,
    70%,
    100% {
      background-position: 50% 0;
    }
    82% {
      background-position: 70% 0;
    }
    92% {
      background-position: 30% 0;
    }
  }
  :global(html[data-motion='paused']) .hero-ink {
    animation: none;
    background-position: 50% 0;
  }
  @media (prefers-reduced-motion: reduce) {
    .hero-ink {
      animation: none;
      background-position: 50% 0;
    }
  }
  @media (forced-colors: active) {
    .hero-ink {
      animation: none;
      background: none;
      color: CanvasText;
      -webkit-text-fill-color: CanvasText;
    }
  }
  .hero-copy {
    position: relative;
    z-index: 2;
    text-align: center;
    padding-inline: 24px;
  }
  h1 {
    font-size: clamp(64px, 7.2vw, 100px);
    line-height: 1.03;
  }
  .hero-copy > p:not(.audience-line) {
    font-size: 17px;
    line-height: 1.65;
    color: #596b84;
    max-width: 550px;
    margin: 25px auto 0;
  }
  .hero-actions {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 25px;
    margin-top: 28px;
  }
  .explore-link {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    padding-block: 15px;
  }
  .explore-link:hover {
    color: #1447e6;
  }
  .audience-line {
    color: #64758d;
    font-size: 11px;
    margin-top: 19px;
  }
  .hero-product-wrap {
    position: relative;
    padding: 42px 24px 0;
    margin-top: 12px;
  }
  .hero-arc {
    position: absolute;
    width: 155%;
    height: 1040px;
    left: -27.5%;
    top: 160px;
    background: #2354e9;
    border-radius: 50% 50% 0 0;
  }
  .product-stage {
    position: relative;
    max-width: 1000px;
    margin: auto;
    padding: 8px;
    border-radius: 18px;
    background: #142b61;
    box-shadow: 0 30px 65px -35px #25497970;
    transform-origin: center top;
  }
  .hero:has(.hero-scene:global([data-sequenced='true'])) {
    padding-top: 0;
  }
  .hero-scene {
    position: relative;
  }
  .hero-curtain {
    display: none;
  }
  .hero-device-group {
    display: contents;
  }
  .hero-scene:global([data-sequenced='true']) {
    height: 100dvh;
    overflow: hidden;
    background: #f8faff;
  }
  .hero-scene:global([data-sequenced='true']) .hero-curtain {
    display: block;
    position: absolute;
    inset: 0;
    z-index: 1;
    background: radial-gradient(ellipse at 50% 10%, #5d83fc, transparent 65%), #2354e9;
    clip-path: ellipse(80% 16% at 50% 109%);
  }
  .hero-scene:global([data-sequenced='true']) .introduction {
    position: absolute;
    inset: 0;
    z-index: 5;
    display: flex;
    align-items: center;
    clip-path: ellipse(80% 0% at 50% 110%);
  }
  .hero-scene:global([data-sequenced='true']) .mobile-hero-caption,
  .hero-scene:global([data-sequenced='true']) .workflow-strip {
    display: none;
  }
  .hero-scene:global([data-sequenced='true']) .hero-copy {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0 24px;
  }
  .hero-scene:global([data-sequenced='true']) h1 {
    margin-top: 0;
  }
  .hero-scene:global([data-sequenced='true']) .hero-actions {
    margin-top: 26px;
    padding-top: 0;
  }
  .hero-scene:global([data-sequenced='true']) .hero-product-wrap {
    position: absolute;
    inset: 0;
    z-index: 3;
    margin: 0;
    padding: 100px 24px 40px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .hero-scene:global([data-sequenced='true']) .hero-product-wrap,
  .hero-scene:global([data-sequenced='true']) .hero-device-group {
    pointer-events: none;
  }
  .hero-scene:global([data-sequenced='true']) :is(.product-stage, .mobile-live-card, [data-product-note]) {
    pointer-events: auto;
  }
  .hero-scene:global([data-sequenced='true']) .hero-arc {
    display: none;
  }
  .hero-scene:global([data-sequenced='true']) .note-class {
    top: calc(50% - 260px);
    right: max(24px, calc((100vw - 1180px) / 2));
  }
  .hero-scene:global([data-sequenced='true']) .note-feedback {
    bottom: calc(50% - 350px);
    left: max(24px, calc((100vw - 1180px) / 2));
  }
  .hero-scene:global([data-sequenced='true']) .product-stage {
    width: min(1000px, calc(100% - 40px));
    flex-shrink: 0;
  }
  .mobile-live-card,
  .mobile-hero-caption {
    display: none;
  }
  :global(.hero-phone) {
    display: none;
  }
  .stage-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 7px 13px 13px;
    color: #e2eaff;
    font-size: 9px;
  }
  .stage-brand {
    display: flex;
    align-items: center;
    gap: 7px;
    font-weight: 500;
  }
  .stage-url {
    color: #b6c8ef;
    padding-right: 100px;
  }
  .stage-dots {
    display: flex;
    gap: 4px;
  }
  .stage-dots i {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: #f4edac;
  }
  .floating-note {
    position: absolute;
    display: flex;
    align-items: center;
    gap: 11px;
    padding: 16px 20px 16px 14px;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 14px 35px -18px #23416a70;
    z-index: 3;
    text-align: left;
  }
  .note-class {
    top: 113px;
    right: max(23px, calc((100vw - 1210px) / 2));
    transform: rotate(3deg);
  }
  .note-feedback {
    bottom: 30px;
    left: max(23px, calc((100vw - 1210px) / 2));
    transform: rotate(-3deg);
  }
  .note-icon {
    width: 37px;
    height: 37px;
    border-radius: 9px;
    color: #1447e6;
    background: #eaf0ff;
    display: grid;
    place-items: center;
    flex-shrink: 0;
  }
  .note-icon.green {
    color: #2d7c63;
    background: #e7f6ee;
  }
  .floating-note strong {
    display: block;
    font-size: 11px;
    font-weight: 550;
  }
  .floating-note div > span {
    display: block;
    font-size: 9px;
    color: #5c6e88;
    margin-top: 4px;
  }
  .workflow-strip {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 25px;
    padding: 44px 30px 46px;
    background: #2354e9;
    color: #eff4ff;
  }
  .workflow-strip > span:not(.connector) {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 12px;
  }
  .connector {
    width: 45px;
    height: 1px;
    background: #ffffff50;
  }
  .introduction {
    background: #ffdbbf;
  }
  .introduction-inner {
    display: grid;
    grid-template-columns: 1.2fr 0.8fr;
    gap: 70px;
    align-items: start;
    padding-block: 115px;
  }
  h2 {
    font-size: clamp(36px, 4.3vw, 54px);
    line-height: 1.13;
  }
  .introduction p {
    font-size: 14px;
    line-height: 1.85;
    color: #4b5b74;
  }
  .introduction p + p {
    margin-top: 18px;
  }
  .teaching-statement {
    padding: 90px 0 48px;
    overflow: hidden;
    background: #172c52;
    color: #f4edac;
  }
  .statement-track {
    display: flex;
    width: max-content;
    align-items: center;
    gap: 0.22em;
    padding-inline: 7vw;
    font-size: clamp(80px, 12vw, 180px);
    line-height: 1.25;
    letter-spacing: -0.04em;
    font-weight: 450;
    white-space: nowrap;
  }
  .statement-serif {
    font-family: 'Instrument Serif', Georgia, serif;
    font-style: italic;
    font-size: 1.15em;
    font-weight: 400;
  }
  .statement-track > svg {
    width: 0.6em;
    height: 0.6em;
    flex-shrink: 0;
    margin-inline: 0.1em;
    color: #ffbc94;
  }
  .statement-bottom {
    display: flex;
    justify-content: space-between;
    align-items: end;
    gap: 30px;
    margin-top: 45px;
  }
  .statement-bottom p {
    color: #d0dbef;
    font-size: 13px;
    line-height: 1.7;
  }
  .scroll-cue {
    display: flex;
    gap: 12px;
    align-items: center;
    font-size: 11px;
    color: #f4edac;
  }
  .people-section {
    padding-block: 110px;
  }
  .people-heading {
    display: flex;
    align-items: end;
    justify-content: space-between;
    gap: 35px;
    margin-bottom: 52px;
  }
  .people-heading h2 {
    max-width: 800px;
  }
  .people-heading > p {
    color: #566a86;
    font-size: 14px;
    line-height: 1.7;
    padding-bottom: 5px;
  }
  .people-layout {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 100px;
    align-items: center;
  }
  .role-copy {
    display: grid;
    gap: 30px;
  }
  .role-copy > div {
    position: relative;
    padding-left: 24px;
  }
  .role-marker {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    position: absolute;
    top: 7px;
    left: 0;
  }
  .blue {
    background: #1447e6;
  }
  .purple {
    background: #9673cd;
  }
  .green {
    background: #55967f;
  }
  .role-copy h3 {
    font-size: 17px;
    font-weight: 550;
    margin-bottom: 10px;
    letter-spacing: -0.02em;
  }
  .role-copy p {
    font-size: 13px;
    line-height: 1.8;
    color: #61718b;
    max-width: 400px;
  }
  .roles-visual {
    background: #edf2fc;
    border-radius: 16px;
    padding: 31px;
  }
  .roles-visual-top {
    display: flex;
    align-items: center;
    gap: 9px;
    font-size: 11px;
    font-weight: 550;
  }
  .role-badge {
    margin-left: auto;
    border: 1px solid #cbd7ed;
    border-radius: 4px;
    padding: 4px 7px;
    color: #5b6f91;
    font-size: 9px;
    font-weight: 400;
  }
  .roles-visual > h3 {
    font-size: 31px;
    letter-spacing: -0.035em;
    font-weight: 500;
    line-height: 1.15;
    margin-top: 30px;
  }
  .role-members {
    margin-top: 24px;
    background: #fff;
    border-radius: 9px;
    padding: 4px 17px;
  }
  .role-members > div {
    display: flex;
    align-items: center;
    gap: 10px;
    padding-block: 15px;
    border-bottom: 1px solid #eef1f8;
    font-size: 11px;
  }
  .role-members > div:last-child {
    border: 0;
  }
  .member-avatar {
    width: 30px;
    height: 30px;
    display: grid;
    place-items: center;
    border-radius: 50%;
    background: #e7edfb;
    color: #59709b;
    font-size: 9px;
  }
  .role-members strong {
    font-weight: 500;
  }
  .role-members div > span:last-of-type {
    margin-left: auto;
    color: #66768f;
    font-size: 9px;
  }
  .role-members :global(svg) {
    color: #4c8975;
  }
  .roles-visual > p {
    color: #687c9d;
    font-size: 10px;
    margin-top: 17px;
    text-align: center;
  }
  .index-heading h2 {
    font-family: 'Instrument Serif', Georgia, serif;
    font-size: clamp(52px, 6vw, 78px);
    font-weight: 400;
  }
  .feature-index {
    background: #f4edac;
    padding-block: 83px;
  }
  .index-heading {
    display: flex;
    justify-content: space-between;
    align-items: end;
    gap: 25px;
    margin-bottom: 53px;
  }
  .index-heading > a {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 12px;
    padding-bottom: 6px;
  }
  .index-columns {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 55px;
  }
  .index-columns h3 {
    font-size: 15px;
    font-weight: 550;
    padding-bottom: 19px;
    border-bottom: 1px solid #d5dfef;
    margin-bottom: 22px;
  }
  .index-columns ul {
    list-style: none;
    padding: 0;
    margin: 0;
    display: grid;
    gap: 17px;
  }
  .index-columns li {
    display: flex;
    align-items: center;
    gap: 10px;
    color: #536986;
    font-size: 12px;
  }
  .index-columns li :global(svg) {
    color: #6b86b7;
    flex-shrink: 0;
  }
  @media (max-width: 1050px) {
    .floating-note {
      display: none;
    }
    .people-layout {
      gap: 45px;
    }
    .stage-url {
      padding-right: 0;
    }
    .index-columns {
      gap: 30px;
    }
  }
  @media (max-width: 760px) {
    .hero:has(.hero-scene:global([data-sequenced='true'])) {
      padding-top: 0;
    }
    .hero-scene:global([data-sequenced='true']) .hero-copy {
      padding: 0 20px;
    }
    .hero-scene:global([data-sequenced='true']) .hero-product-wrap {
      padding: 100px 24px 140px;
    }
    .hero-scene:global([data-sequenced='true']) .hero-curtain {
      background:
        radial-gradient(ellipse at 45% 0%, #688bff88, transparent 60%), linear-gradient(180deg, #3264f1, #1b44c8);
    }
    .hero-scene:global([data-sequenced='true']) .hero-device-group {
      display: block;
      position: relative;
      width: min(290px, calc(100% - 40px), calc((100svh - 250px) * 0.493));
    }
    .hero-scene:global([data-sequenced='true']) .product-stage {
      width: 100%;
    }
    .hero-scene:global([data-sequenced='true']) .mobile-live-card {
      right: -18px;
      bottom: -112px;
      width: min(236px, 88%);
      padding: 13px 15px;
    }

    .content-width {
      width: calc(100% - 40px);
    }
    .hero {
      padding-top: 51px;
    }
    h1 {
      font-size: clamp(40px, 12.3vw, 78px);
    }
    .hero-serif {
      font-size: 1.32em;
    }
    .teaching-statement {
      padding: 48px 0 30px;
    }
    .statement-track {
      font-size: 80px;
      padding-inline: 20px;
    }
    .statement-bottom {
      margin-top: 28px;
    }
    .statement-bottom p {
      font-size: 11px;
    }
    .scroll-cue {
      font-size: 9px;
      gap: 8px;
    }
    .hero-copy > p:not(.audience-line) {
      font-size: 16px;
      max-width: 340px;
      margin-top: 21px;
    }
    .hero-actions {
      gap: 18px;
      margin-top: 24px;
    }
    .explore-link {
      gap: 7px;
      font-size: 11px;
    }
    .audience-line {
      font-size: 10px;
      margin-top: 15px;
    }
    .hero-product-wrap {
      padding: 40px 14px 154px;
      margin-top: 4px;
    }
    .product-stage {
      width: min(300px, calc(100% - 40px));
      padding: 0;
      border-radius: 0;
      background: none;
      box-shadow: none;
    }
    .desktop-screen {
      display: none;
    }
    :global(.hero-phone) {
      display: block;
    }
    .stage-bar {
      display: none;
    }
    .stage-url {
      display: none;
    }
    .hero-arc {
      top: 150px;
      bottom: -180px;
      width: 180%;
      left: -40%;
      height: auto;
      background:
        radial-gradient(ellipse at 45% 0%, #688bff88, transparent 46%), linear-gradient(180deg, #3264f1, #1b44c8 85%);
    }
    .hero-arc::after {
      content: '';
      position: absolute;
      inset: 0;
      background-image:
        linear-gradient(#ffffff15 1px, transparent 1px), linear-gradient(90deg, #ffffff15 1px, transparent 1px);
      background-size: 48px 48px;
      mask-image: radial-gradient(ellipse at 50% 45%, #000, transparent 68%);
    }
    .mobile-live-card {
      display: block;
      position: absolute;
      right: max(24px, calc((100% - 440px) / 2));
      bottom: 20px;
      width: min(270px, calc(100% - 48px));
      padding: 18px;
      border: 1px solid #fff;
      border-radius: 16px;
      background: #f8faff;
      box-shadow:
        0 20px 45px -18px #10296470,
        0 2px 5px #10296410;
      text-align: left;
      z-index: 3;
    }
    .live-card-label {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      font-size: 10px;
      color: #435b83;
    }
    .live-card-label > span:first-child {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      color: #1447e6;
      font-weight: 500;
    }
    .live-card-label > span:last-child {
      padding: 3px 6px;
      background: #e8eefb;
      border-radius: 4px;
    }
    .mobile-live-card h3 {
      font-size: 19px;
      font-weight: 550;
      letter-spacing: -0.03em;
      margin: 12px 0 3px;
    }
    .mobile-live-card p {
      font-size: 11px;
      color: #62718a;
    }
    .live-card-bottom {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-top: 13px;
      border-top: 1px solid #dfe6f3;
      padding-top: 10px;
    }
    .live-card-bottom > span {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 10px;
      color: #435b83;
    }
    .live-card-bottom a {
      display: grid;
      place-items: center;
      min-height: 36px;
      padding: 0 12px;
      border-radius: 8px;
      background: #1447e6;
      color: #fff;
      font-size: 12px;
      font-weight: 500;
    }
    .live-card-bottom a:hover {
      background: #0f37b8;
    }
    .live-card-bottom a:focus-visible {
      outline: 2px solid #1447e6;
      outline-offset: 2px;
    }
    .mobile-hero-caption {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 5px;
      position: relative;
      padding: 16px 24px 72px;
      background: #1b44c8;
      color: #e2eaff;
      font-size: clamp(17px, 4vw, 21px);
      line-height: 1.4;
      letter-spacing: -0.02em;
    }
    .mobile-hero-caption strong {
      color: #f4edac;
      font-family: 'Instrument Serif', Georgia, serif;
      font-size: 35px;
      line-height: 1.15;
      font-weight: 400;
      font-style: italic;
    }
    .hero::after {
      content: '';
      position: absolute;
      bottom: -1px;
      left: -10%;
      width: 120%;
      height: 30px;
      border-radius: 50% 50% 0 0 / 100% 100% 0 0;
      background: #ffdbbf;
      z-index: 2;
    }
    .workflow-strip {
      display: none;
    }
    .workflow-strip > span:not(.connector) {
      flex: 1;
      flex-direction: column;
      gap: 10px;
      text-align: center;
      font-size: 9px;
      line-height: 1.7;
    }
    .connector {
      width: 15px;
      margin-top: 10px;
    }
    .introduction-inner {
      grid-template-columns: 1fr;
      gap: 24px;
      padding-block: 65px;
    }
    .hero-scene:global([data-sequenced='true']) .introduction {
      align-items: flex-start;
      padding-top: 52px;
    }
    h2 {
      font-size: 36px;
    }
    .introduction p {
      font-size: 13px;
      line-height: 1.85;
    }
    .introduction p + p {
      margin-top: 14px;
    }
    .people-section {
      padding-block: 64px;
    }
    .people-heading {
      display: block;
      margin-bottom: 32px;
    }
    .people-heading h2 {
      font-size: 35px;
    }
    .people-heading > p {
      margin-top: 18px;
      font-size: 13px;
    }
    .people-layout {
      grid-template-columns: 1fr;
      gap: 36px;
    }
    .role-copy {
      gap: 26px;
    }
    .role-copy h3 {
      font-size: 16px;
    }
    .role-copy p {
      font-size: 14px;
    }
    .roles-visual {
      padding: 24px;
    }
    .roles-visual > h3 {
      font-size: 30px;
    }
    .role-members {
      padding-inline: 12px;
    }
    .role-members > div {
      gap: 8px;
      font-size: 10px;
    }
    .role-members div > span:last-of-type {
      font-size: 8px;
    }
    .feature-index {
      padding-block: 57px;
    }
    .index-heading {
      display: block;
      margin-bottom: 33px;
    }
    .index-heading > a {
      margin-top: 20px;
      font-size: 12px;
    }
    .index-columns {
      grid-template-columns: 1fr;
      gap: 35px;
    }
    .index-columns h3 {
      padding-bottom: 13px;
      margin-bottom: 18px;
    }
    .index-columns ul {
      grid-template-columns: 1fr 1fr;
      gap: 14px 18px;
    }
    .index-columns li {
      font-size: 12px;
      gap: 6px;
      align-items: start;
      line-height: 1.5;
    }
    .index-columns li :global(svg) {
      width: 12px;
      height: 15px;
    }
  }
  :global(html[data-motion='paused']) .statement-track {
    width: auto;
    white-space: normal;
    flex-wrap: wrap;
    justify-content: center;
    font-size: clamp(52px, 9vw, 130px);
    text-align: center;
  }
  :global(html[data-motion='paused']) .statement-track > svg {
    display: none;
  }
  @media (prefers-reduced-motion: reduce) {
    .statement-track {
      width: auto;
      white-space: normal;
      flex-wrap: wrap;
      justify-content: center;
      font-size: clamp(52px, 9vw, 130px);
      text-align: center;
    }
    .statement-track > svg {
      display: none;
    }
  }
</style>
